import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconPlus, IconTrash, IconBuildingBank } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const ACCOUNT_TYPE_LABEL = { checking: 'Conta Corrente', savings: 'Poupança' };

const BankAccounts = () => {
    const [appId, setAppId] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [banks, setBanks] = useState([]);
    const [agencies, setAgencies] = useState([]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ bank: '', agency: '', account_number: '', account_digit: '', account_type: 'checking' });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
            setCompanyId(res.data.default_company_id);
        })();
    }, []);

    useEffect(() => {
        if (!appId || !companyId) return;
        fetchBankAccounts();
        fetchBanks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, companyId]);

    const fetchBankAccounts = async () => {
        const res = await api.get(`v1/apps/${appId}/bank-accounts/?company=${companyId}`);
        setBankAccounts(res.data.bank_accounts || []);
    };

    const fetchBanks = async () => {
        const res = await api.get(`v1/apps/${appId}/banks/`);
        setBanks(res.data || []);
    };

    const fetchAgencies = async (bankId) => {
        if (!bankId) {
            setAgencies([]);
            return;
        }
        const res = await api.get(`v1/apps/${appId}/bank-agencies/?bank=${bankId}`);
        setAgencies(res.data || []);
    };

    const handleOpenDialog = () => {
        setForm({ bank: '', agency: '', account_number: '', account_digit: '', account_type: 'checking' });
        setAgencies([]);
        setStatus({ type: '', message: '' });
        setDialogOpen(true);
    };

    const handleBankChange = (bankId) => {
        setForm((prev) => ({ ...prev, bank: bankId, agency: '' }));
        fetchAgencies(bankId);
    };

    const handleSave = async () => {
        if (!form.bank || !form.agency) {
            setStatus({ type: 'error', message: 'Selecione o banco e a agência' });
            return;
        }
        if (!form.account_number) {
            setStatus({ type: 'error', message: 'Informe o número da conta' });
            return;
        }
        setSaving(true);
        try {
            await api.post(`v1/apps/${appId}/bank-accounts/`, { ...form, company: companyId });
            setDialogOpen(false);
            fetchBankAccounts();
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar conta bancária' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (bankAccount) => {
        if (!window.confirm(`Excluir a conta bancária "${bankAccount.bank_name} - Ag ${bankAccount.agency_number}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/bank-accounts/${bankAccount.id}/`);
            setBankAccounts((prev) => prev.filter((b) => b.id !== bankAccount.id));
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir conta bancária' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuildingBank}
                gradient="blue"
                title="Contas Bancárias"
                subtitle="Contas da empresa, usadas em pagamentos, recebimentos e conciliação"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenDialog}>
                        Nova Conta
                    </Button>
                }
            />

            {status.message && !dialogOpen && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Banco</TableCell>
                                <TableCell>Agência</TableCell>
                                <TableCell>Conta</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {bankAccounts.map((ba) => (
                                <TableRow key={ba.id}>
                                    <TableCell>
                                        {ba.bank_code} - {ba.bank_name}
                                    </TableCell>
                                    <TableCell>{ba.agency_number}</TableCell>
                                    <TableCell>
                                        {ba.account_number}
                                        {ba.account_digit ? `-${ba.account_digit}` : ''}
                                    </TableCell>
                                    <TableCell>{ACCOUNT_TYPE_LABEL[ba.account_type] || ba.account_type}</TableCell>
                                    <TableCell>
                                        <Chip label={ba.is_active ? 'Ativa' : 'Inativa'} color={ba.is_active ? 'success' : 'default'} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleDelete(ba)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {bankAccounts.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Nenhuma conta bancária cadastrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova Conta Bancária</DialogTitle>
                <DialogContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Banco"
                                fullWidth
                                required
                                value={form.bank}
                                onChange={(e) => handleBankChange(e.target.value)}
                            >
                                {banks.map((b) => (
                                    <MenuItem key={b.id} value={b.id}>
                                        {b.code} - {b.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Agência"
                                fullWidth
                                required
                                disabled={!form.bank}
                                value={form.agency}
                                onChange={(e) => setForm({ ...form, agency: e.target.value })}
                                helperText={form.bank && agencies.length === 0 ? 'Nenhuma agência cadastrada para este banco' : ''}
                            >
                                {agencies.map((a) => (
                                    <MenuItem key={a.id} value={a.id}>
                                        {a.number}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                label="Número da conta"
                                fullWidth
                                required
                                value={form.account_number}
                                onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Dígito"
                                fullWidth
                                value={form.account_digit}
                                onChange={(e) => setForm({ ...form, account_digit: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Tipo"
                                fullWidth
                                value={form.account_type}
                                onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                            >
                                <MenuItem value="checking">Conta Corrente</MenuItem>
                                <MenuItem value="savings">Poupança</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Salvando...' : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BankAccounts;
